import { gql } from "@apollo/client";

// The organization-wide reads, for the dashboard.
//
// They hang off `viewer.account.organization` rather than off a bucket, and
// that is the whole difference: the server composes the scope from the resolved
// account and forces the orgId dimension, where the bucket reads force orgId
// AND bucketId. There is no variable on any query below that could carry either
// — see the tenancy note on OrganizationStatisticsQueries in the api schema.

// The organization's statistics, binned or grouped.
//
// `truncated` and `interval` are selected deliberately, not for completeness.
// truncated says the answer was partial; interval is the bin width the server
// actually used, which is not always the one asked for — an axis labelled from
// the request rather than the response is labelled wrong.
export const aggregate = gql`
  query OrganizationStatisticsAggregate(
    $input: OrganizationStatisticsAggregateInput
    $filter: OrganizationStatisticsFilterInput
  ) {
    viewer {
      id
      account {
        id
        organization {
          id
          statistics {
            id
            aggregate(input: $input, filter: $filter) {
              id
              interval
              intervalMs
              start
              end
              bins
              measures
              truncated
              series {
                id
                key
                eventType
                dimensions
                points
                totals
                count
              }
            }
          }
        }
      }
    }
  }
`;

// The same shape off a different point: how many buckets, files, images and
// zips arrived or left, rather than how many bytes.
//
// A separate query rather than a second field on the one above, because they
// are separate Timestream reads with separate failure modes — the counts
// panel going red must not blank the charts, and a point with nothing on it
// yet must not stop the other from rendering.
export const counts = gql`
  query OrganizationStatisticsCounts(
    $input: OrganizationStatisticsAggregateInput
    $filter: OrganizationStatisticsFilterInput
  ) {
    viewer {
      id
      account {
        id
        organization {
          id
          statistics {
            id
            counts(input: $input, filter: $filter) {
              id
              interval
              intervalMs
              start
              end
              bins
              measures
              truncated
              series {
                id
                key
                eventType
                dimensions
                points
                totals
                count
              }
            }
          }
        }
      }
    }
  }
`;

// What the organization's own limit allows against what it has consumed.
//
// The one read on the dashboard that is NOT a delta, and the only absolute
// figure on it. Everything on the capture path is a net change over a window,
// and datum clamps that window to 31 days — which is why "how much does this
// organization hold" cannot be answered there at all. A limit's usage is
// aggregated over the limit's own period instead; for the organization's size
// limit that is the year.
//
// `status` is selected because it is the field that makes the numbers safe to
// render: a null usage means "period not implemented" or "the aggregate
// failed", and drawing either as a 0% bar reads as full headroom.
export const usage = gql`
  query OrganizationUsageList($input: ListInput) {
    viewer {
      id
      account {
        id
        organization {
          id
          usage {
            id
            list(input: $input) {
              id
              last
              truncated
              items {
                id
                limitId
                limitDescription
                key
                measure
                type
                period
                threshold
                limit
                usage
                ratio
                breached
                status
                windowStart
                windowEnd
              }
            }
          }
        }
      }
    }
  }
`;
